"use client";

import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { PreOrderStatus, type PreOrderOutput } from "@/interfaces/pre-orders";
import { PreOrderService } from "@/services/PreOrderService";
import { useNavigate, useParams } from "react-router-dom";
import { OrderConfirmationStep } from "./components/OrderConfirmationStep";
import { numberToMoney } from "@/util/moneyConversion";
import { PreOrderDetailsView } from "./components/PreOrderDetailsView";

import type { OrderOutput } from "@/interfaces/orders";


export default function PreOrderFlow() {
  const navigate = useNavigate();
  const [preOrder, setPreOrder] = useState<PreOrderOutput | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const preOrderService = new PreOrderService();
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    fetchPreOrder(id!);
  }, [id]);

  const fetchPreOrder = async (id: string) => {
    setIsLoading(true);
    try {
      const preOrderData = await preOrderService.findById(id, {
        expand: "offers",
      });
      if (!preOrderData) {
        navigate("/pre-pedidos");
        return;
      }

      setPreOrder(preOrderData);
      setIsLoading(false);
    } catch (error) {
      console.error("Erro ao buscar pré-pedido:", error);
      setIsLoading(false);
    }
  };

  const handleOrderStep: (order: OrderOutput | null) => void = () => {
    try {
      // O endpoint /accept já criou o pedido e atualizou o status do pré-pedido
      navigate("/pre-pedidos");
    } catch (error) {
      console.error("Erro ao processar pedido:", error);
    }
  };

  const handleRejectOrder = async () => {
    try {
      // O endpoint /reject já atualizou o status do pré-pedido
      // Redireciona de volta para a lista de pré-pedidos
      navigate("/pre-pedidos");
    } catch (error) {
      console.error("Erro ao rejeitar pedido:", error);
    }
  };

  const handleFinish = () => {
    navigate("/pre-pedidos");
  };

  const handleBack = () => {
    navigate("/pre-pedidos");
  };

  if (isLoading || !preOrder) {
    return (
      <div className="container mx-auto py-12 flex justify-center items-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500 mr-2" />
        <span className="text-gray-600">Carregando dados do pedido...</span>
      </div>
    );
  }

  // If the pre-order is already accepted or rejected, show the details view
  if (
    preOrder.status === PreOrderStatus.ACCEPTED ||
    preOrder.status === PreOrderStatus.REJECTED
  ) {
    return (
      <div className="container mx-auto py-6 px-4 max-w-5xl">
        <PreOrderDetailsView preOrder={preOrder} onBack={handleFinish} />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4 max-w-5xl">
      <div className="mb-8">
        <h1 className="text-xl font-medium mb-2 text-gray-800">
          Confirmação de Pré-Pedido
        </h1>
        <div className="flex flex-wrap gap-2 items-center text-gray-500">
          <span>Código: {preOrder.id}</span>
          <span>•</span>
          <span>Cliente: {preOrder.customer.name}</span>
          <span>•</span>
          <span className="font-medium text-gray-700">
            Valor Total: {numberToMoney(preOrder.totalAmount)}
          </span>
          {preOrder.delivery.deliveryValue && preOrder.delivery.deliveryValue > 0 && (
            <>
              <span>•</span>
              <span className="font-medium text-gray-700">
                Valor da Entrega: {numberToMoney(preOrder.delivery.deliveryValue)}
              </span>
            </>
          )}
          {preOrder.tax && preOrder.tax > 0 && (
            <>
              <span>•</span>
              <span className="font-medium text-gray-700">
                Imposto: {numberToMoney(preOrder.tax)}
              </span>
            </>
          )}
        </div>
      </div>

      <OrderConfirmationStep
        preOrder={preOrder}
        customer={{
          id: preOrder.customer.customerId || "temp",
          name: preOrder.customer.name,
          ddd: preOrder.customer.ddd || "",
          phoneNumber: preOrder.customer.phoneNumber || "",
          addresses: preOrder.delivery.address ? [preOrder.delivery.address] : [],
          createdAt: new Date(),
          updatedAt: new Date(),
        }}
        selectedAddressIndex={0}
        onNext={handleOrderStep}
        onBack={handleBack}
        onReject={handleRejectOrder}
      />
    </div>
  );
}
